/**
 * ПАМЯТЬ МЕНЮ (`s713`, Д-08 приёмки штаба 05.10.2026: «меню сохраняет выбранный раздел,
 * раскрытые группы и прокрутку при переходах по рабочей области; не сбрасывается в
 * „Операционку“»).
 *
 * ⛔ КОРЕНЬ. Каркас панели (`AppShell`) рисует каждая страница сама, поэтому при каждом
 * переходе он создавался заново — а выбор раздела, раскрытый пункт и прокрутка меню жили
 * в его состоянии и пропадали. Раздел после перехода вычислялся по адресу; у адреса,
 * которого в меню нет, брался первый видимый раздел — «Операционка».
 *
 * Теперь эти три величины лежат здесь, вне каркаса, и переживают переход. Раздел меняет
 * только человек (список разделов в меню); раскрытый пункт — только его нажатие в меню.
 * Открытие страницы заново (обновление, ссылка) определяет раздел по адресу, как раньше;
 * для адреса вне меню берётся последний раздел этой вкладки.
 */
interface Pamyat {
  /** Выбранный раздел; `undefined` — ещё не определён (страница только открыта). */
  gruppa: string | undefined;
  /** Раскрытый пункт: ключ, `null` — все свёрнуты, `undefined` — по адресу. */
  punkt: string | null | undefined;
}

const KLYUCH_VKLADKI = 'opsmind:menyu:razdel';

let pamyat: Pamyat = { gruppa: undefined, punkt: undefined };
let prokrutka = 0;
const slushateli = new Set<() => void>();

function soobshchit(): void {
  slushateli.forEach((s) => s());
}

export const menyuPamyat = {
  podpisatsya(slushatel: () => void): () => void {
    slushateli.add(slushatel);

    return () => {
      slushateli.delete(slushatel);
    };
  },

  snimok(): Pamyat {
    return pamyat;
  },

  zapomnit(chast: Partial<Pamyat>): void {
    pamyat = { ...pamyat, ...chast };

    if (typeof chast.gruppa === 'string') {
      try {
        sessionStorage.setItem(KLYUCH_VKLADKI, chast.gruppa);
      } catch {
        // Хранилище вкладки недоступно — память в пределах страницы всё равно работает.
      }
    }

    soobshchit();
  },

  /** Последний раздел этой вкладки — для страницы, открытой заново по адресу вне меню. */
  razdelVkladki(): string | null {
    try {
      return sessionStorage.getItem(KLYUCH_VKLADKI);
    } catch {
      return null;
    }
  },

  prokrutka(): number {
    return prokrutka;
  },

  zapomnitProkrutku(vysota: number): void {
    prokrutka = vysota;
  },

  /** Чистый лист — как после открытия страницы заново. Нужен проверкам. */
  sbrosit(): void {
    pamyat = { gruppa: undefined, punkt: undefined };
    prokrutka = 0;

    try {
      sessionStorage.removeItem(KLYUCH_VKLADKI);
    } catch {
      // см. выше
    }

    soobshchit();
  },
};

/**
 * ПУНКТ МЕНЮ ОСТАЁТСЯ ВЫБРАННЫМ, ПОКА ПОВЕРХ ЕГО ЭКРАНА ОТКРЫТА КАРТОЧКА (`s722`, приёмка владельца
 * 06.10.2026: «Сохранить выбранный раздел меню»).
 *
 * Карточка записи, открытая с «Доски записей», живёт по адресу `/zapis/N`; карточка лида — по
 * `/doska-lidov/N`. Пункт меню сравнивался с адресом точно и на время карточки гас: меню выглядело
 * так, будто раздел сброшен. Откуда карточку открыли, говорит сам переход (`state.otkuda`); у
 * адреса «экран/номер» без этого слова хозяин — экран.
 */
export function punktPodKartochkoy(to: string, pathname: string, otkuda: unknown): boolean {
  const put = to.split('?')[0];
  if (put === '' || put === '/' || pathname === put) return false;
  if (typeof otkuda === 'string') return otkuda.split('?')[0] === put;

  return pathname.startsWith(`${put}/`) && /^\d+$/.test(pathname.slice(put.length + 1));
}
